import {
  addQueryParamsToUrl,
  generateUrl,
  updatePathParamsForApiUrl
} from '../../utils/UrlGenerator'

import HttpConstants from '../../constants/HttpConstants'
import UrlConstants from '../../constants/UrlConstants'

// Define interfaces
export default interface CryptoKeyPair {
  publicKey: CryptoKey
  privateKey: CryptoKey
}

export class ClientEncryptionService {
  private keyPair: CryptoKeyPair | null = null
  private serverPublicKey: CryptoKey | null = null
  private clientId: string | null = null
  private isKeyExchanging = false

  async initialize() {
    // Generate client's key pair
    this.isKeyExchanging = true
    this.keyPair = await crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-256'
      },
      true,
      ['encrypt', 'decrypt']
    )

    // Export public key for exchange
    const exportedPublicKey = await this.exportPublicKey(this.keyPair.publicKey)

    let { url } = updatePathParamsForApiUrl(
      generateUrl(UrlConstants.ENCRYPTION.USECASE) as string,
      ''
    )
    url = addQueryParamsToUrl(url, {}, HttpConstants.POST_METHOD)

    const response = await fetch(`${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-encrypted': 'false'
      },
      body: JSON.stringify({
        clientPublicKey: exportedPublicKey
      })
    })

    const { serverPublicKey, clientId } = await response.json().catch((err) => {
      this.isKeyExchanging = false
      throw new Error('Unable to establish secure connection', err)
    })

    this.serverPublicKey = await this.importPublicKey(serverPublicKey)
    this.clientId = clientId
    this.isKeyExchanging = false

    return clientId
  }

  async exportPublicKey(key: CryptoKey): Promise<JsonWebKey> {
    return await crypto.subtle.exportKey('jwk', key)
  }

  async importPublicKey(jwk: JsonWebKey): Promise<CryptoKey> {
    return await crypto.subtle.importKey(
      'jwk',
      jwk,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256'
      },
      true,
      ['encrypt']
    )
  }

  getClientId(): string | null {
    return this.clientId
  }

  getIsKeyExchange(): boolean {
    return this.isKeyExchanging
  }

  getServerPublicKey(): CryptoKey | null {
    return this.serverPublicKey
  }

  async encryptRequest(data: any) {
    if (!this.serverPublicKey) {
      throw new Error('Server public key not available')
    }

    // Generate session key
    const sessionKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt']
    )
    const iv = crypto.getRandomValues(new Uint8Array(12))

    const encryptedFiles = []
    const encryptedFields: any = {}

    if (data instanceof FormData) {
      for (const [key, value] of data.entries()) {
        if (typeof value === 'string') {
          const encrypted = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            sessionKey,
            new TextEncoder().encode(value)
          )
          encryptedFields[key] = Array.from(new Uint8Array(encrypted)) // Convert to Base64
        }
      }

      for (const [key, file] of data.entries()) {
        if (file instanceof File) {
          const fileBuffer = await file.arrayBuffer()
          const encryptedFile = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            sessionKey,
            fileBuffer
          )

          encryptedFiles.push({
            name: file.name,
            type: file.type,
            data: Array.from(new Uint8Array(encryptedFile)), // Convert to Base64
            key
          })
        }
      }
    }

    // No file, encrypt the form data as JSON
    const encodedData = new TextEncoder().encode(JSON.stringify(data))

    const encryptedData = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      sessionKey,
      encodedData
    )

    // Encrypt session key with server's public key
    const exportedSessionKey = await crypto.subtle.exportKey('raw', sessionKey)
    const encryptedSessionKey = await crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      this.serverPublicKey,
      exportedSessionKey
    )

    const encryptedIv = await crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      this.serverPublicKey,
      iv
    )
    return {
      ed: Array.from(new Uint8Array(encryptedData)),
      esk: Array.from(new Uint8Array(encryptedSessionKey)),
      eiv: Array.from(new Uint8Array(encryptedIv)),
      ef: encryptedFiles,
      efd: encryptedFields
    }
  }

  async decryptResponse(encryptedResponse: {
    ed: number[]
    esk: number[]
    eiv: number[]
    filters?: string[]
  }) {
    const svgRegex = /<svg[\s\S]*?>[\s\S]*?<\/svg>/i

    const responseString = JSON.stringify(encryptedResponse)

    if (svgRegex.test(responseString)) {
      return encryptedResponse
    }
    if (!this.keyPair) {
      throw new Error('Client not initialized')
    }

    // Decrypt session key using client's private key
    const decryptedSessionKey = await crypto.subtle.decrypt(
      { name: 'RSA-OAEP' },
      this.keyPair.privateKey,
      new Uint8Array(encryptedResponse.esk)
    )

    const decryptedIv = await crypto.subtle.decrypt(
      { name: 'RSA-OAEP' },
      this.keyPair.privateKey,
      new Uint8Array(encryptedResponse.eiv)
    )

    // Import session key
    const sessionKey = await crypto.subtle.importKey(
      'raw',
      decryptedSessionKey,
      'AES-GCM',
      false,
      ['decrypt']
    )

    // Decrypt data using session key
    const decryptedData = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: new Uint8Array(decryptedIv)
      },
      sessionKey,
      new Uint8Array(encryptedResponse.ed)
    )

    const parsedData = JSON.parse(new TextDecoder().decode(decryptedData))

    const result = {
      ...parsedData,
      ...(encryptedResponse.filters
        ? { filters: encryptedResponse.filters }
        : {})
    }
    return result
  }
}
